/**
 * Страница «Что нового» — заметки о релизах, свежие сверху.
 *
 * Содержимое приезжает из markdown-файлов `notes/*.md` (см. releases.ts).
 * Открытие страницы гасит подсветку кнопки в рельсе: самый свежий релиз
 * помечается прочитанным.
 */
import { useEffect } from 'react';
import { Empty, Tag } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { setReleasesSeen } from '../../shared/prefs/store.ts';
import { LATEST_RELEASE, RELEASES } from './releases.ts';
import styles from './ReleasesPage.module.css';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

function formatDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function ReleasesPage(): JSX.Element {
  useDocumentTitle('Что нового');
  useEffect(() => {
    if (LATEST_RELEASE) setReleasesSeen(LATEST_RELEASE.id);
  }, []);

  return (
    <div data-testid="releases-page">
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>Что нового</h2>
      </div>
      <div className={styles.pageSubtitle}>Изменения Lens по релизам, свежие сверху</div>

      {RELEASES.length === 0 ? (
        <Empty description="Заметок о релизах пока нет" data-testid="releases-empty" />
      ) : (
        <div className={styles.list} data-testid="releases-list">
          {RELEASES.map((release) => (
            <article key={release.id} className={styles.release} data-testid={`release-${release.id}`}>
              <div className={styles.meta}>
                {release.version ? (
                  <Tag className={styles.version}>{release.version}</Tag>
                ) : null}
                <span className={styles.date}>{formatDate(release.date)}</span>
              </div>
              <div className={styles.content}>
                <h3 className={styles.title}>{release.title}</h3>
                <div className={styles.body}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{release.body}</ReactMarkdown>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
